import { useLocation } from "@solidjs/router";
import { ArrowUpRight, Bell, ChevronRight, Search } from "lucide-solid";
import { For, Show, createEffect, createMemo, createResource } from "solid-js";
import type { JSX } from "solid-js";

import { InstallAppButton } from "@/components/pwa/InstallAppButton";
import { useAuth } from "@/features/auth/AuthProvider";
import { settingsApi } from "@/features/settings/api";
import { hydrateTenantLogo, rememberedLogo } from "@/features/settings/logo-memory";
import { AppearanceButton } from "@/features/theme/AppearanceButton";
import { bindThemeTenant } from "@/features/theme/apply";
import { sessionTenantId } from "@/features/theme/storage";

import { MobileNav, Sidebar } from "./Sidebar";

const ROUTE_LABELS: Record<string, string> = {
  dashboard: "Tableau de bord",
  calendar: "Calendrier",
  opportunities: "Suivi",
  formations: "Formations",
  sessions: "Sessions",
  learners: "Apprenants",
  enrollments: "Inscriptions",
  employers: "Employeurs",
  invoices: "Factures",
  rooms: "Salles",
  partners: "Partenaires",
  reports: "Rapports",
  budget: "Budget & précompta",
  tva: "Récap TVA",
  eduqua: "Audit eduQua",
  health: "Santé système",
  attendance: "Émargement",
  settings: "Paramètres centre",
  access: "Habilitations",
  platform: "Éditeur",
  schools: "Écoles",
};

function humanize(seg: string): string {
  if (ROUTE_LABELS[seg]) return ROUTE_LABELS[seg];
  // Si segment ressemble à un UUID ou un id → "Détail"
  if (/^[0-9a-f]{8}-/i.test(seg)) return "Détail";
  return seg.charAt(0).toUpperCase() + seg.slice(1);
}

export function AppShell(props: { children: JSX.Element }) {
  const location = useLocation();
  const auth = useAuth();
  const [settings] = createResource(() => settingsApi.get().catch(() => null));
  createEffect(() => {
    const id = settings()?.id;
    if (!id) return;
    void hydrateTenantLogo(id, () => settingsApi.fetchLogo()).catch(() => {});
  });
  createEffect(() => {
    if (settings.loading) return;
    bindThemeTenant(settings()?.id ?? sessionTenantId());
  });
  const headerLogo = () =>
    rememberedLogo(settings()?.id) ??
    settingsApi.logoPublicUrl(settings()?.logo_storage_path ?? null);
  const catalogHref = () => {
    const subdomain = settings()?.subdomain;
    if (!subdomain || auth.platformAdmin()) return null;
    return `/public/c/${subdomain}`;
  };

  const breadcrumbs = createMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    if (segments.length === 0) return [{ label: "Tableau de bord", href: "/" }];
    return segments.map((seg, i) => ({
      label: humanize(seg),
      href: `/${segments.slice(0, i + 1).join("/")}`,
    }));
  });

  const pageTitle = createMemo(() => {
    const crumbs = breadcrumbs();
    return crumbs[crumbs.length - 1]?.label ?? "Scola";
  });

  const userInitials = createMemo(() => {
    const email = auth.user()?.email ?? "";
    return email.slice(0, 2).toUpperCase();
  });

  return (
    <div class="min-h-screen flex bg-[var(--bg-base)] text-[var(--fg-default)]">
      <Sidebar />
      <main class="flex-1 min-w-0 flex flex-col">
        <header class="sticky top-0 z-20 relative border-b border-[var(--header-border)] bg-[var(--bg-header)] text-[var(--header-fg)] after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gradient-to-r after:from-transparent after:via-[var(--accent-base)] after:to-transparent">
          <div class="h-[60px] px-4 md:px-8 flex items-center justify-between gap-4">
            {/* Fil d'Ariane */}
            <div class="min-w-0 flex items-center gap-3">
              <Show when={headerLogo()}>
                {(url) => (
                  <img
                    src={url()}
                    alt=""
                    class="h-8 w-8 shrink-0 rounded-[8px] bg-white object-contain p-0.5 ring-1 ring-[var(--border-default)] lg:hidden"
                  />
                )}
              </Show>
              <nav
                aria-label="Fil d'Ariane"
                class="hidden lg:flex items-center gap-1.5 text-[13px] text-[var(--header-fg-muted)]"
              >
                <For each={breadcrumbs()}>
                  {(crumb, i) => (
                    <>
                      <Show when={i() > 0}>
                        <ChevronRight
                          size={14}
                          stroke-width={1.75}
                          class="text-[var(--fg-subtle)]"
                        />
                      </Show>
                      <a
                        href={crumb.href}
                        class={`truncate rounded-md px-1.5 py-0.5 transition hover:bg-[var(--bg-inset)] hover:text-[var(--header-fg)] ${
                          i() === breadcrumbs().length - 1
                            ? "font-semibold text-[var(--header-fg)]"
                            : ""
                        }`}
                      >
                        {crumb.label}
                      </a>
                    </>
                  )}
                </For>
              </nav>
              <h1 class="font-display text-lg font-semibold text-[var(--header-fg)] leading-tight truncate lg:hidden">
                {pageTitle()}
              </h1>
            </div>

            {/* Actions globales */}
            <div class="flex items-center gap-2">
              <InstallAppButton />
              <AppearanceButton />
              <button
                type="button"
                aria-label="Recherche globale"
                class="hidden lg:inline-flex h-9 items-center gap-2 rounded-[10px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-3 text-[13px] text-[var(--fg-muted)] shadow-[0_1px_2px_rgba(11,18,32,0.04)] transition hover:border-[var(--border-strong)] hover:text-[var(--fg-default)]"
              >
                <Search size={14} stroke-width={1.75} />
                <span>Rechercher</span>
                <kbd class="ml-3 rounded-[5px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-1.5 py-[1px] font-mono text-[10px] text-[var(--fg-subtle)]">
                  ⌘K
                </kbd>
              </button>

              <button
                type="button"
                aria-label="Notifications"
                class="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--fg-muted)] shadow-[0_1px_2px_rgba(11,18,32,0.04)] transition hover:border-[var(--border-strong)] hover:text-[var(--fg-strong)]"
              >
                <Bell size={16} stroke-width={1.75} />
              </button>

              <Show when={catalogHref()}>
                {(href) => (
                  <a
                    href={href()}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="hidden sm:inline-flex h-9 items-center gap-1.5 rounded-[10px] bg-[var(--fg-strong)] px-3.5 text-[13px] font-semibold text-[var(--bg-base)] shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] transition hover:bg-[var(--accent-alt)] hover:text-[var(--accent-fg)]"
                  >
                    Catalogue public
                    <ArrowUpRight size={14} stroke-width={2} />
                  </a>
                )}
              </Show>

              <div
                class="ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--accent-alt-soft)] text-[11px] font-bold text-[var(--accent-alt)] ring-2 ring-[var(--bg-surface)] shadow-[0_0_0_1px_var(--border-default)]"
                title={auth.user()?.email ?? ""}
              >
                {userInitials()}
              </div>
            </div>
          </div>
        </header>
        <div class="flex-1 w-full">
          <div class="mx-auto w-full max-w-[1360px] px-4 pb-28 pt-6 md:px-8 lg:pb-12 lg:pt-8">
            {props.children}
          </div>
        </div>
      </main>
      <MobileNav />
    </div>
  );
}
